import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    clearStoredAuth,
    getStoredExpiresAt,
    getStoredToken,
    markUserActivity,
    maybeRefreshToken,
    setPaymentRequiredHandler,
    setStoredToken,
    setUnauthorizedHandler,
} from "../api/client";
import { auth as authApi } from "../api/endpoints";

const AuthContext = createContext(null);

// Exported so unit tests can inject a mock context value without
// having to stand up the full AuthProvider (with its real API calls).
export { AuthContext };

const REFRESH_POLL_MS = 60 * 1000; // every minute, the refresh helper checks freshness

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => getStoredToken());
    const [expiresAt, setExpiresAt] = useState(() => getStoredExpiresAt());
    const [user, setUser] = useState(null);
    const [bootstrapping, setBootstrapping] = useState(Boolean(getStoredToken()));
    const [error, setError] = useState(null);

    // ── Activity listeners (used for proactive refresh gating) ────────
    useEffect(() => {
        const onActivity = () => markUserActivity();
        window.addEventListener("mousedown", onActivity);
        window.addEventListener("keydown", onActivity);
        window.addEventListener("focus", onActivity);
        return () => {
            window.removeEventListener("mousedown", onActivity);
            window.removeEventListener("keydown", onActivity);
            window.removeEventListener("focus", onActivity);
        };
    }, []);

    // ── On 401 from any API call, axios interceptor calls this. ──────
    const purge = useCallback(() => {
        clearStoredAuth();
        setToken("");
        setExpiresAt(0);
        setUser(null);
    }, []);

    useEffect(() => {
        setUnauthorizedHandler(() => {
            purge();
        });
        // Phase 16 — subscription gate. On 402, route to the
        // SubscriptionRequired page WITHOUT clearing the session (the
        // token is still valid; only the entitlement is missing).
        setPaymentRequiredHandler(() => {
            if (typeof window !== "undefined" && window.location) {
                if (!window.location.pathname.startsWith("/subscription-required")) {
                    window.location.assign("/subscription-required");
                }
            }
        });
        return () => {
            setUnauthorizedHandler(null);
            setPaymentRequiredHandler(null);
        };
    }, [purge]);

    // ── Bootstrap: if we have a token in storage, try /api/auth/me. ──
    useEffect(() => {
        if (!getStoredToken()) {
            setBootstrapping(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const me = await authApi.me();
                if (!cancelled) {
                    setUser(me);
                    setError(null);
                }
            } catch (_e) {
                if (!cancelled) {
                    purge();
                }
            } finally {
                if (!cancelled) setBootstrapping(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [purge]);

    // ── Background refresh poller ────────────────────────────────────
    useEffect(() => {
        if (!token) return;
        const id = setInterval(async () => {
            const refreshed = await maybeRefreshToken();
            if (refreshed && refreshed.access_token) {
                setToken(refreshed.access_token);
                setExpiresAt(Date.parse(refreshed.expires_at));
                if (refreshed.user) setUser(refreshed.user);
            }
        }, REFRESH_POLL_MS);
        return () => clearInterval(id);
    }, [token]);

    /* ────────────────────────────── actions ────────────────────────────── */
    const login = useCallback(async (username, password) => {
        setError(null);
        const data = await authApi.login(username, password);
        setStoredToken(data.access_token, data.expires_at);
        markUserActivity();
        setToken(data.access_token);
        setExpiresAt(Date.parse(data.expires_at));
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await authApi.logout();
        } catch (_e) {
            // best-effort
        }
        purge();
    }, [purge]);

    const refresh = useCallback(async () => {
        const data = await authApi.refresh();
        setStoredToken(data.access_token, data.expires_at);
        setToken(data.access_token);
        setExpiresAt(Date.parse(data.expires_at));
        if (data.user) setUser(data.user);
        return data;
    }, []);

    const changePassword = useCallback(async (current_password, new_password) => {
        await authApi.changePassword(current_password, new_password);
        // Server has bumped pw_ver — our current token is now invalid.
        // Caller is responsible for navigating to /login.
    }, []);

    const value = useMemo(
        () => ({
            token,
            user,
            expiresAt,
            isAuthenticated: Boolean(token && user),
            bootstrapping,
            error,
            login,
            logout,
            refresh,
            changePassword,
            isAdmin: user?.role === "admin" || user?.role === "superadmin",
            isSuperadmin: user?.role === "superadmin" || user?.role === "admin",
            mustChangePassword: Boolean(user?.must_change_password),
            setUser,
        }),
        [token, user, expiresAt, bootstrapping, error, login, logout, refresh, changePassword],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used inside <AuthProvider>");
    }
    return ctx;
}
